import React from 'react';
import { Col, Row, Select, Tooltip, Typography } from 'antd';
import {
	CalendarOutlined,
	ClockCircleOutlined,
	FieldTimeOutlined,
	QuestionCircleOutlined,
} from '@ant-design/icons';
import momentTimeZone from 'moment-timezone';
import { AICampaignFormData } from '../types';

const { Title } = Typography;

interface StepFiveProps {
	handleInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
	formData: AICampaignFormData;
	handleRadioChange: (name: keyof AICampaignFormData, value: string) => void;
	handleDaySelection: (selectedDays: string[]) => void;
	handleSliderChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleTimeZoneChange: (value: string) => void;
}

const StepFive: React.FC<StepFiveProps> = ({
	handleInputChange,
	formData,
	handleRadioChange,
	handleDaySelection,
	handleSliderChange,
	handleTimeZoneChange,
}) => {
	// Time zones
	const timezones = momentTimeZone.tz.names();
	// Days of the week
	const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

	return (
		<div className="step-content">
			<Title level={2}>When do you want your campaign to run?</Title>
			<p className="text-gray-600 mb-6">
				Set the schedule for when your AI will contact leads. Choose times that your team can follow
				up if needed.
			</p>

			{/* Time Zone Selection */}
			<div className="mb-6">
				<label className="block text-sm font-medium text-gray-700 mb-2">Time Zone</label>

				<Select
					showSearch
					value={formData.timezone}
					placeholder="Select a time zone"
					style={{ width: '100%' }}
					onChange={handleTimeZoneChange}
					options={timezones.map((zone) => ({ label: zone, value: zone }))}
					filterOption={(input, option) =>
						(option?.label as string).toLowerCase().includes(input.toLowerCase())
					}
				/>
			</div>

			{/* Schedule Type Selection */}
			<div className="mb-6">
				<label className="block text-sm font-medium text-gray-700 mb-2">Schedule Type</label>
				<div className="space-y-3">
					{[
						{
							type: 'business',
							icon: <ClockCircleOutlined className="text-lg" />,
							label: 'Business Hours',
							desc: 'Monday-Friday, 9 AM - 5 PM',
						},
						{
							type: 'extended',
							icon: <FieldTimeOutlined className="text-lg" />,
							label: 'Extended Hours',
							desc: 'Monday-Friday, 8 AM - 8 PM',
						},
						{
							type: 'custom',
							icon: <CalendarOutlined className="text-lg" />,
							label: 'Custom Schedule',
							desc: 'Choose your own days and times',
						},
					].map(({ type, icon, label, desc }) => (
						<label
							key={type}
							className={`block p-4 border rounded-lg cursor-pointer ${formData.scheduleType === type ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
						>
							<div className="flex items-center">
								<input
									type="radio"
									name="scheduleType"
									value={type}
									checked={formData.scheduleType === type}
									onChange={() => handleRadioChange('scheduleType', type)}
									style={{ display: 'none' }}
								/>
								<div
									className={`w-6 h-6 mr-3 ${formData.scheduleType === type ? 'text-blue-500' : 'text-gray-500'}`}
								>
									{icon}
								</div>
								<div>
									<div className="font-medium">{label}</div>
									<div className="text-sm text-gray-500">{desc}</div>
								</div>
							</div>
						</label>
					))}
				</div>
			</div>

			{/* Custom Schedule Options */}
			{formData.scheduleType === 'custom' && (
				<div className="mb-6">
					<label className="block text-sm font-medium text-gray-700 mb-2">
						Select Days{' '}
						<Tooltip title="Days when calls are allowed">
							<QuestionCircleOutlined />
						</Tooltip>
					</label>
					<Select
						mode="multiple"
						allowClear
						placeholder="Please select"
						value={formData.selectedDays}
						onChange={handleDaySelection}
						className="w-full"
						options={daysOfWeek.map((day) => ({ label: day, value: day }))}
					/>

					{/* Start and End Time */}
					<Row gutter={8}>
						<Col lg={12} sm={24}>
							<div className="mt-4">
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Start Time{' '}
									<Tooltip title="Daily call start time">
										<QuestionCircleOutlined />
									</Tooltip>
								</label>
								<input
									type="time"
									name="startTime"
									value={formData.startTime}
									onChange={handleInputChange}
									className="w-full p-2 border border-gray-300 rounded-md"
								/>
							</div>
						</Col>
						<Col lg={12} sm={24}>
							<div className="mt-4">
								<label className="block text-sm font-medium text-gray-700 mb-2">
									End Time{' '}
									<Tooltip title="Daily call end time">
										<QuestionCircleOutlined />
									</Tooltip>
								</label>
								<input
									type="time"
									name="endTime"
									value={formData.endTime}
									onChange={handleInputChange}
									className="w-full p-2 border border-gray-300 rounded-md"
								/>
							</div>
						</Col>
					</Row>
				</div>
			)}
			<div className="mb-6">
				<Row gutter={8}>
					<Col lg={12} sm={24}>
						<div className="mt-4">
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Maximum Retries{' '}
								<Tooltip title="Number of times to attempt reaching a contact">
									<QuestionCircleOutlined />
								</Tooltip>
							</label>
							<input
								type="number"
								name="maximumRetries"
								min={0}
								value={formData.maximumRetries}
								onChange={handleInputChange}
								className="w-full p-2 border border-gray-300 rounded-md"
							/>
						</div>
					</Col>
					<Col lg={12} sm={24}>
						<div className="mt-4">
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Hours Between Retries{' '}
								<Tooltip title="Minimum waiting period between attempts">
									<QuestionCircleOutlined />
								</Tooltip>
							</label>
							<input
								type="number"
								min={0}
								name="hoursBetweenRetries"
								value={formData.hoursBetweenRetries}
								onChange={handleInputChange}
								className="w-full p-2 border border-gray-300 rounded-md"
							/>
						</div>
					</Col>
				</Row>
			</div>

			{/* Maximum Calls Per Day */}
			<div className="mb-6">
				<label className="text-sm font-medium text-gray-700 mb-2">
					Maximum calls per day: {formData.maxCallsPerDay}
				</label>
				<input
					type="range"
					min="20"
					max="100"
					step="1"
					value={formData.maxCallsPerDay}
					onChange={handleSliderChange}
					className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
				/>
				<div className="flex justify-between text-xs text-gray-500 mt-1">
					<span>20 calls</span>
					<span>100 calls</span>
				</div>
			</div>
			<div className="p-4 bg-yellow-50 border border-yellow-100 rounded-md mb-6">
				<div className="flex">
					<div className="text-yellow-600 mr-3">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-6 w-6"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
							/>
						</svg>
					</div>
					<div className="text-sm text-yellow-700">
						We recommend starting with a lower volume to test your campaign's effectiveness. You can
						always increase volume later.
					</div>
				</div>
			</div>
		</div>
	);
};

export default StepFive;
